import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { MsalGuard, MsalInterceptor, MsalModule, MsalService } from '@azure/msal-angular';
import { InteractionType, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { ModalModule } from 'ngx-bootstrap/modal';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CalendarModule } from 'primeng/calendar';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ProgressBarModule } from 'primeng/progressbar';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { TreeModule } from 'primeng/tree';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SectionContainerComponent } from './sectionContainer/section-container/section-container.component';
import { CreateSendProductReportComponent } from './sections/createSendProductReport/create-send-product-report/create-send-product-report-component';
import { ResizableDirective } from './sections/createSendProductReport/resizable.directive';
import { MeddraComponent } from './sections/meddra/meddra/meddra.component';
import { MedicinalProductComponent } from './sections/medicinalProduct/medicinal-product/medicinal-product.component';
import { WebtraderComponent } from './sections/webtrader/webtrader/webtrader.component';
import { GlobalErrorHandler } from './shared/common/GlobalErrorHandler';
import { DatetimePickerComponent } from './shared/component/datetime-picker/datetime-picker.component';
import { DetailedCreatedSendProductReportViewComponent } from './shared/component/detailed-view/detailed-create-send-product-report/detailed-create-send-product-report-view.component';
import { DetailedMeddraViewComponent } from './shared/component/detailed-view/detailed-meddra-view/detailed-meddra-view.component';
import { DetailedMedicinalProductViewComponent } from './shared/component/detailed-view/detailed-medicinal-product-view/detailed-medicinal-product-view.component';
import { DetailedWebtraderViewComponent } from './shared/component/detailed-view/detailed-webtrader-view/detailed-webtrader-view.component';
import { DetailedPostViewComponent } from './shared/component/detailed-view/detailed-post-view/detailed-post-view.component';
import { DisclaimerComponent } from './shared/component/disclaimer/disclaimer.component';
import { FileSelectorComponent } from './shared/component/file-selector/file-selector.component';
import { FileValueAccessor } from './shared/component/file-selector/file-value-accessor';
import { FormlyDetailedViewComponent } from './shared/component/formly-detailed-view/formly-detailed-view.component';
import { FormlyDrugIngredientsViewComponent } from './shared/component/formly-drug-ingredients-view/formly-drug-ingredients-view.component';
import { ListDetailedAddNodesComponent } from './shared/component/list-detailed-add-nodes/list-detailed-add-nodes.component';
import { ListDetailedViewComponent } from './shared/component/list-detailed-view/list-detailed-view.component';
import { LoadingComponent } from './shared/component/loading/loading.component';
import { LocalFileSelectorComponent } from './shared/component/local-file-selector/local-file-selector.component';
import { LookupFinderComponent } from './shared/component/lookup-finder/lookup-finder.component';
import { MenuComponent } from './shared/component/menu/menu.component';
import { ModalContentComponent } from './shared/component/modal-content/modal-content.component';
import { ModalWrapperComponent } from './shared/component/modal-wrapper/modal-wrapper.component';
import { ModeToggleComponent } from './shared/component/mode-toggle/mode-toggle/mode-toggle.component';
import { OrganisationSelectorComponent } from './shared/component/organisation-selector/organisation-selector.component';
import { PaginationComponent } from './shared/component/pagination/pagination.component';
import { StaticFinderComponent } from './shared/component/static-finder/static-finder.component';
import { MeddraToolboxComponent } from './shared/component/toolbox/meddra-toolbox/meddra-toolbox.component';
import { MedicinalProductToolboxComponent } from './shared/component/toolbox/medicinal-product-toolbox/medicinal-product-toolbox.component';
import { ProductToolboxComponent } from './shared/component/toolbox/product-toolbox/product-toolbox.component';
import { WebtraderToolboxComponent } from './shared/component/toolbox/webtrader-toolbox/webtrader-toolbox.component';
import { PostToolboxComponent } from './shared/component/toolbox/post-toolbox/post-toolbox.component';
import { TreeViewComponent } from './shared/component/tree-view/tree-view.component';
import { UserInfoComponent } from './shared/component/user-info/user-info.component';
import { VersionComponent } from './shared/component/version/version.component';
import { ExportServiceComponent } from './shared/service/export-service/export.service';
import { LookupGetService } from './shared/service/lookup-service/lookup-get.service';
import { XevprmSubmissionService } from './shared/service/xevprm-submission-service/xevprm-submission.service';
import { ItemFinderComponent } from './shared/component/item-finder/item-finder.component';
import { FieldQueryTypeComponent } from './shared/type/field-query-type/field-query-type.component';
import { ConditionQueryInputTypeComponent } from './shared/type/condition-query-input-type/condition-query-input-type.component';
import { ConditionQueryDatetimeTypeComponent } from './shared/type/condition-query-datetime-type/condition-query-datetime-type/condition-query-datetime-type.component';
import { ConditionQuerySelectTypeComponent } from './shared/type/condition-query-select-type/condition-query-select-type/condition-query-select-type.component';
import { ConditionQueryLookupTypeComponent } from './shared/type/condition-query-lookup-type/condition-query-lookup-type/condition-query-lookup-type.component';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { ItemFinderSearchBarComponent } from './shared/component/item-finder/item-finder-search-bar/item-finder-search-bar.component';
import { ItemFinderArrayModelComponent } from './shared/component/item-finder/item-finder-array-model/item-finder-array-model.component';
import { ItemFinderTreeModelComponent } from './shared/component/item-finder/item-finder-tree-model/item-finder-tree-model.component';
import { DisclaimerItemFinderComponent } from './shared/component/item-finder/disclaimer-item-finder/disclaimer-item-finder.component';
import { FormYesnodatetimeFieldComponent } from './shared/type/form-yesnodatetime-field/form-yesnodatetime-field.component';
import { FormDateFieldComponent } from './shared/type/form-date-field/form-date-field.component';
import { FormDateTimeFieldComponent } from './shared/type/form-datetime-field/form-datetime-field.component';
import { DocumentViewTypeComponent } from './shared/type/document-view-type/document-view-type.component';
import { FormNullifiednodatetimeFieldComponent } from './shared/type/form-nullifiednodatetime-field/form-nullifiednodatetime-field.component';
import { DiffViewerComponent } from './shared/component/diff-viewer/diff-viewer/diff-viewer.component';
import { ConditionQuerySelectTypeWithDefaultValueComponent } from './shared/type/condition-query-select-type-with-default-value/condition-query-select-type-with-default-value.component';
import { FileListDetailedViewComponent } from './shared/component/file-list-detailed-view/file-list-detailed-view/file-list-detailed-view.component';
import { AuthInterceptor } from './shared/service/auth-service/auth.interceptor';
import { ConditionQueryLookupInputTypeComponent } from './shared/type/condition-query-lookup-input-type/condition-query-lookup-input-type.component';
import { SessionExpiredInfoComponent } from './shared/component/session-expired-info/session-expired-info.component';
import { RenderWidthDirective } from './sections/medicinalProduct/render-width.directive';
import { PostComponent } from './sections/post/post.component';
import { SelectTypeFieldComponent } from './shared/type/select-type-field/select-type-field/select-type-field.component';
import { FormlySelectModule } from '@ngx-formly/core/select';
import { ResizableColumnDirective } from './shared/component/static-finder/resizable-column.directive';
import { BulkLookupSelectorTypeComponent } from './shared/type/bulk-lookup-selector-type/bulk-lookup-selector-type/bulk-lookup-selector-type.component';
import { BulkExportComponent } from './sections/bulkExport/bulk-export/bulk-export.component';
import { BulkUpdateComponent } from './sections/bulkUpdate/bulk-update/bulk-update.component';
import { DetailedBulkExportViewComponent } from './shared/component/detailed-view/detailed-bulk-export-view/detailed-bulk-export-view.component';
import { BulkExportToolboxComponent } from './shared/component/toolbox/bulk-export-toolbox/bulk-export-toolbox.component';
import { FormlyBulkExportViewComponent } from './shared/component/formly-bulk-export-view/formly-bulk-export-view.component';
import { MatStepperModule } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { BulkRadioTypeComponent } from './shared/type/bulk-radio-type/bulk-radio-type.component';
import { BulkDateTypeComponent } from './shared/type/bulk-date-type/bulk-date-type.component';
import { BulkLookupListTypeComponent } from './shared/type/bulk-lookup-list-type/bulk-lookup-list-type.component';
import { BulkRepeaterTypeComponent } from './shared/type/bulk-repeater-type/bulk-repeater-type.component';
import { DetailedBulkUpdateViewComponent } from './shared/component/detailed-view/detailed-bulk-update-view/detailed-bulk-update-view.component';
import { BulkUpdateToolboxComponent } from './shared/component/toolbox/bulk-update-toolbox/bulk-update-toolbox.component';
import { FormlyBulkUpdateViewComponent } from './shared/component/formly-bulk-update-view/formly-bulk-update-view.component';
import { MaskedInputTypeComponent } from './shared/type/masked-input-type/masked-input-type/masked-input-type.component';
import { RequestViewerComponent } from './shared/component/request-viewer/request-viewer/request-viewer.component';
import { MaintenanceService } from './shared/service/maintenance-service/maintenance.service'; 
import { MaintenanceComponent } from './shared/component/maintenance/maintenance.component';
import { BulkUpdateRepeaterComponent } from './shared/type/bulk-update-repeater/bulk-update-repeater.component';
import { BulkUpdateActionTypeComponent } from './shared/type/bulk-update-action-type/bulk-update-action-type.component';
import { BulkUpdateFilterRepeaterTypeComponent } from './shared/type/bulk-update-filter-repeater-type/bulk-update-filter-repeater-type.component';
import { BulkUpdateChangeRepeaterTypeComponent } from './shared/type/bulk-update-change-repeater-type/bulk-update-change-repeater-type.component';
import { InpselTypeComponent } from './shared/type/inpsel-type/inpsel-type.component';
import { LinkTypeComponent } from './shared/type/link-type/link-type.component';

const msalConfig = {
  auth: {
    clientId: environment.clientId,
    authority: environment.authority,
    redirectUri: environment.redirectUri
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Info,
      loggerCallback: (level: any, message: any, containsPii: any) => {
        if (!containsPii) {

        }
      },
    },
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false
  }
};

@NgModule({
  declarations: [
    AppComponent,
    PaginationComponent,
    MenuComponent,
    UserInfoComponent,
    FileSelectorComponent,
    FileValueAccessor,
    ModalContentComponent,
    DatetimePickerComponent,
    OrganisationSelectorComponent,
    ModalWrapperComponent,
    DisclaimerComponent,
    LoadingComponent,
    ResizableDirective,
    CreateSendProductReportComponent,
    DetailedCreatedSendProductReportViewComponent,
    DetailedMeddraViewComponent,
    DetailedMedicinalProductViewComponent,
    DetailedWebtraderViewComponent,
    DetailedPostViewComponent,
    ListDetailedViewComponent,
    FormlyDetailedViewComponent,
    TreeViewComponent,
    ProductToolboxComponent,
    WebtraderToolboxComponent,
    MedicinalProductToolboxComponent,
    MeddraToolboxComponent,
    PostToolboxComponent,
    VersionComponent,
    StaticFinderComponent,
    LookupFinderComponent,
    FormlyDrugIngredientsViewComponent,
    ModeToggleComponent,
    ListDetailedAddNodesComponent,
    WebtraderComponent,
    SectionContainerComponent,
    MedicinalProductComponent,
    MeddraComponent,
    LocalFileSelectorComponent,
    ItemFinderComponent,
    FieldQueryTypeComponent,
    ConditionQueryInputTypeComponent,
    ConditionQueryDatetimeTypeComponent,
    ConditionQuerySelectTypeComponent,
    ConditionQueryLookupTypeComponent,
    ItemFinderSearchBarComponent,
    ItemFinderArrayModelComponent,
    ItemFinderTreeModelComponent,
    DisclaimerItemFinderComponent,
    FormYesnodatetimeFieldComponent,
    FormDateFieldComponent,
    FormDateTimeFieldComponent,
    DocumentViewTypeComponent,
    FormNullifiednodatetimeFieldComponent,
    DiffViewerComponent,
    ConditionQuerySelectTypeWithDefaultValueComponent,
    FileListDetailedViewComponent,
    ConditionQueryLookupInputTypeComponent,
    SessionExpiredInfoComponent,
    RenderWidthDirective,
    PostComponent,
    SelectTypeFieldComponent,
    ResizableColumnDirective,
    BulkLookupSelectorTypeComponent,
    BulkExportComponent,
    BulkUpdateComponent,
    DetailedBulkExportViewComponent,
    BulkExportToolboxComponent,
    FormlyBulkExportViewComponent,
    BulkRadioTypeComponent,
    BulkDateTypeComponent,
    BulkLookupListTypeComponent,
    BulkRepeaterTypeComponent,
    DetailedBulkUpdateViewComponent,
    BulkUpdateToolboxComponent,
    FormlyBulkUpdateViewComponent,
    MaskedInputTypeComponent,
    RequestViewerComponent,
    MaintenanceComponent,
    BulkUpdateRepeaterComponent,
    BulkUpdateActionTypeComponent,
    BulkUpdateFilterRepeaterTypeComponent,
    BulkUpdateChangeRepeaterTypeComponent,
    InpselTypeComponent,
    LinkTypeComponent
  ],
  imports: [
    ClipboardModule,
    BrowserModule,
    ConfirmDialogModule,
    ProgressBarModule,
    DialogModule,
    ToastModule,
    CalendarModule,
    TreeModule,
    BrowserAnimationsModule,
    BrowserModule,
    CommonModule,
    ReactiveFormsModule,
    MatStepperModule, 
    MatButtonModule,
    MatInputModule,
    FormlyModule,
    FormlyMaterialModule,
    HttpClientModule,
    MsalModule.forRoot(new PublicClientApplication(msalConfig), {
      interactionType: InteractionType.Redirect, // MSAL Guard Configuration
      authRequest: {
        scopes: ['user.read']
      }
    }, {
      interactionType: InteractionType.Redirect, // MSAL Interceptor Configuration
      protectedResourceMap: new Map([
        ['https://graph.microsoft.com/v1.0/me', ['user.read']]
      ])
    }),
    AppRoutingModule,
    RouterModule.forRoot([]),
    ReactiveFormsModule,  
    FormsModule,
    FormlySelectModule,
    
    ModalModule.forRoot(),
    FormlyModule.forRoot({
      validators: [
        {
          name: 'minLengthConditionsForMeddra',
          validation: (control: AbstractControl): ValidationErrors | null => {
            const raw = control.value;
            if (raw == null || raw === '') return null;
            const parts = String(raw).split('|').map(p => p.trim());
            const idx = parts.findIndex(p => p.length > 10);
            return idx >= 0
              ? { minLengthConditionsForMeddra: { message: `Number too big: ${parts[idx].length} digits on a 10 digits field.`, index: idx, value: parts[idx] } }
              : null;
          }
        },
        {
          name: 'isNumber',
          validation: (control: AbstractControl): ValidationErrors | null => {

            const raw = control.value;
            if (raw == null || raw === '') return null;
            const parts = String(raw).split('|').map(p => p.trim());
            const idx = parts.findIndex(p => p !== '' && Number.isNaN(Number(p)));
            return idx >= 0
              ? { isNumber: { message: 'A number should be entered.', index: idx, value: parts[idx] } }
              : null;

          }
        },
        {
          name: 'notMultipleValues',
          validation: (control: AbstractControl) => !(control.value !== undefined && control.value.split(/[\s,;]+/).length > 1) ? null : { 'notMultipleValues': 'Searching for multiple codes is not allowed.' }
        }
      ],
      validationMessages: [
        { name: 'required', message: 'Field is mandatory / must have a specified value' },
        { name: 'minLength', message: 'Minimum length is ${minLength} characters' },
        { name: 'maxLength', message: 'Maximum length is ${maxLength} characters' },
        { name: 'isNumber', message: 'A number should be entered.' },
        { name: 'minLengthConditionsForMeddra', message: (error: any, field: FormlyFieldConfig) => `Number too big:  ${field.formControl?.value.length} digits on a 10 digits field.` },
        { name: 'notMultipleValues', message: 'Searching for multiple codes is not allowed.' },
        // Add more custom validation messages here
      ],
      wrappers: [
        { name: "modal-wrapper", component: ModalWrapperComponent }
      ],
      types: [
        { name: 'masked-input', component: MaskedInputTypeComponent, wrappers: ['form-field'] },
        { name: 'repeatUpdate', component: BulkUpdateRepeaterComponent },
        { name: 'repeat', component: BulkRepeaterTypeComponent },
        { name: 'bulkUpdateFilterRepeaterType', component: BulkUpdateFilterRepeaterTypeComponent },
        { name: 'bulkUpdateChangeRepeaterType', component: BulkUpdateChangeRepeaterTypeComponent },
        { name: 'datetime', component: DatetimePickerComponent },
        { name: 'file', component: FileSelectorComponent, wrappers: ['form-field'] },
        { name: 'fieldQueryType', component: FieldQueryTypeComponent },
        { name: 'conditionQueryInputType', component: ConditionQueryInputTypeComponent },
        { name: 'conditionQueryDatetimeType', component: ConditionQueryDatetimeTypeComponent },
        { name: 'conditionQuerySelectType', component: ConditionQuerySelectTypeComponent },
        { name: 'conditionQueryLookupType', component: ConditionQueryLookupTypeComponent },
        { name: 'yesNoDatetimeType', component: FormYesnodatetimeFieldComponent },
        { name: 'nullifiedNoDatetimeType', component: FormNullifiednodatetimeFieldComponent },
        { name: 'dateType', component: FormDateFieldComponent },
        { name: 'datetimeType', component: FormDateTimeFieldComponent },
        { name: 'documentViewType', component: DocumentViewTypeComponent },
        { name: 'conditionQuerySelectTypeWithDefaultValue', component: ConditionQuerySelectTypeWithDefaultValueComponent },
        { name: 'conditionQueryLookupInputType', component: ConditionQueryLookupInputTypeComponent},
        { name: 'selectType', component: SelectTypeFieldComponent, extends: 'select'},
        { name: 'bulkLookupSelectorType', component: BulkLookupSelectorTypeComponent,  wrappers: ['form-field']},
        { name: 'bulkRadioType', component: BulkRadioTypeComponent },
        { name: 'bulkUpdateActionType', component: BulkUpdateActionTypeComponent },
        { name: 'bulkDateType', component: BulkDateTypeComponent },
        { name: 'bulkLookupListType', component: BulkLookupListTypeComponent },
        { name: 'inpselType', component:InpselTypeComponent }, 
        { name: 'linkType', component: LinkTypeComponent }       
      ],

    }),
    FormlyBootstrapModule,
    HttpClientModule,
  ],
  exports: [SelectTypeFieldComponent],
  providers: [
    Clipboard,
    ConfirmationService,
    MessageService,
    LookupGetService,
    XevprmSubmissionService,
    ExportServiceComponent,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    MsalGuard,
    MsalService,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    MaintenanceService
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
